import {
  BenchmarkDisablingDefaultThreshold,
  MAX_NEW_USER_PENDING_ATTEMPTS,
} from '@scale/shared/defaults';

// @ts-ignore FIXME

// you could imagine adding other metadata in the future:
// suggested value, which task types it's relevant for, etc.

// exact same path as config
export const definitions = {
  configSettingsValues: {
    productVertical: {
      definition: 'The product vertical that owns this project.',
      advice:
        'If you notice that this is wrong for a given project, change this! Better yet, consider changing the config at the task-type level, or task-type-task-customer level instead.',
    },
    graderMatcherParams: {
      definition:
        'Parameters to configure the grader matcher, which determines how to pair annotations before grading them.',
      advice:
        'Must be a map from task type to grader matcher function name/threshold, for example {"lineannotation": {"matcherFunction": "frechetLineDistance", "threshold": 10}}',
    },
    graderTransformerParams: {
      definition:
        'Parameters to configure the grader transformer, which determines how to transform responses to gradeable annotations.',
      advice:
        'Must be a map from task type to an objects of configs, for example {"segmentannotation": {"downsampleFactor": 4}}',
    },
    graderContextParams: {
      definition:
        'Parameters to configure the grader context, which determines whether attempter labels are equivalent to ground truth labels.',
      advice:
        'An example could be {"labelMustBeAcceptable": true, "labelMatchThreshold": 3, "originalPathWeight": 1, "correctPathWeight": 2, "customLabelMatches": { "Automobile": ["Truck", "SUV"], "Boat": ["Vessel", "Buoy"]}}. Note we use display names and not the choices.',
    },
    benchmarkRateAttempt: {
      definition: 'The rate at which benchmarks are served to taskers at the attempt level.',
      advice:
        'Typically set at 0.05-0.1, which means 5-10% of tasks a tasker gets in a queue will be a benchmark (on average).',
    },
    benchmarkRateL0: {
      definition: 'The rate at which benchmarks are served to taskers at the L0 review level.',
      advice:
        'Typically set at 0.1, which means 10% of tasks a tasker gets in a queue will be a benchmark (on average).',
    },
    benchmarkRateL1: {
      definition: 'The rate at which benchmarks are served to taskers at the L1 review level.',
      advice:
        'Typically set at 0.1, which means 10% of tasks a tasker gets in a queue will be a benchmark (on average).',
    },
    benchmarkRateReviewConsensus: {
      definition:
        'The rate at which benchmarks are served to taskers at the ReviewConsensus (L4) review level.',
      advice:
        'Typically set at 0.1, which means 10% of tasks a tasker gets in a queue will be a benchmark (on average).',
    },
    benchmarkRateL10: {
      definition: 'The rate at which benchmarks are served to taskers at the L10 review level.',
      advice:
        'Typically set at 0.1, which means 10% of tasks a tasker gets in a queue will be a benchmark (on average).',
    },
    canaryBenchmarksConfig: {
      definition:
        'The limit of number of attempts on benchmarks before disabling them to surface and choose to either to enable them or stay disabled. NOTE: This is only enabled for benchmarks created through the LLM Control Center.',
      advice:
        'Typically set at 20 (should always be > 0), which means 20 attempts on a benchmark will be submitted before disabling it.',
    },
    onlyServeWallBenchmarksWithinWall: {
      definition: 'Only serve wall benchmarks within the benchmark wall.',
      advice:
        'When this config is on, only benchmarks that are marked as wall benchmarks can be served within the wall. When this config is off, wall benchmarks will be prioritized but regular benchmarks can be served if wall ones are exhausted.',
    },
    passBenchmarkWallOnConsecutivePassedBenchmarksAttempt: {
      definition:
        'Allows for taskers to pass a Benchmark Wall at L-1 level if they pass this many consensuctive benchmarks.',
      advice:
        'This is an integer value set at a value below Attempt: min benchmarks. Use this config in conjunction with wallBenchmarkPassThresholdAttempt to allow for good taskers to pass the wall earlier.',
    },
    wallBenchmarkPassThresholdAttempt: {
      definition: 'The accuracy required to pass a benchmark within the wall',
      advice:
        'This config is a decimal value ranging between 0 and 1. By default accuracy required to pass a benchmark is set on the trainingScenario itself, but that can be overriden by this config.',
    },
    decayedBenchmarkWallSizeAttempt: {
      definition:
        'The rate at which the benchmark serving rate decays for taskers at the attempt level.',
      advice:
        'This decay number is based on the number of benchmarks that a tasker has done (including wall benchmarks) and decays the serving rate linearly from 0.7 to a floor of the benchmarkRateAttempt once the tasker has done decayedBenchmarkWallSizeAttempt number of benchmarks.',
    },
    decayedBenchmarkWallSizeL0: {
      definition:
        'The rate at which the benchmark serving rate decays for taskers at the L0 level.',
      advice:
        'This decay number is based on the number of benchmarks that a tasker has done (including wall benchmarks) and decays the serving rate linearly from 0.7 to a floor of the benchmarkRateL0 once the tasker has done decayedBenchmarkWallSizeL0 number of benchmarks.',
    },
    decayedBenchmarkWallSizeL1: {
      definition:
        'The rate at which the benchmark serving rate decays for taskers at the L1 level.',
      advice:
        'This decay number is based on the number of benchmarks that a tasker has done (including wall benchmarks) and decays the serving rate linearly from 0.7 to a floor of the benchmarkRateL1 once the tasker has done decayedBenchmarkWallSizeL1 number of benchmarks.',
    },
    decayedBenchmarkWallSizeL4: {
      definition:
        'The rate at which the benchmark serving rate decays for taskers at the L4 level.',
      advice:
        'This decay number is based on the number of benchmarks that a tasker has done (including wall benchmarks) and decays the serving rate linearly from 0.7 to a floor of the benchmarkRateL4 once the tasker has done decayedBenchmarkWallSizeL4 number of benchmarks.',
    },
    decayedBenchmarkWallSizeL10: {
      definition:
        'The rate at which the benchmark serving rate decays for taskers at the L10 level.',
      advice:
        'This decay number is based on the number of benchmarks that a tasker has done (including wall benchmarks) and decays the serving rate linearly from 0.7 to a floor of the benchmarkRateL10 once the tasker has done decayedBenchmarkWallSizeL10 number of benchmarks.',
    },
    serveReviewBenchmarksToAttempters: {
      definition:
        'Serves review level benchmarks at a specified review level to attempters instead of attempt level benchmarks.',
      advice:
        'This config is primarily used to leverage benchmarks to provide a quality signal for attempers (which typically do not have benchmark coverage on RLHF queues). Set at the numerical review level for which you have reviewer benchmarks on, typically 4 for chat projects.',
    },
    disableDirectlyFromReview: {
      definition:
        'Whether taskers that perform poorly on reviews should be disabled instead of demoted by default.',
    },
    benchmarkRateQIRAuditor: {
      definition:
        'The rate at which benchmarks are served to taskers at the QIR Auditor review level (Pipeline V3).',
      advice:
        'Typically set at 0.1, which means 10% of tasks a tasker gets in a queue will be a benchmark (on average).',
    },
    benchmarkRateQIRLeadAuditor: {
      definition:
        'The rate at which benchmarks are served to taskers at the QIR Lead Auditor review level (Pipeline V3).',
      advice:
        'Typically set at 0.1, which means 10% of tasks a tasker gets in a queue will be a benchmark (on average).',
    },
    percentInitialInstructiveBenchmarks: {
      definition:
        'The percentage of instructive benchmarks (instructive = tasker can view the answer key after submitting) within the initial "wall" of benchmarks.',
      advice:
        'Set this pretty high (0.85 is default). This initial wall of benchmarks is partly to help taskers learn how to task, which they can only do if the BM is instructive. Note that there must actually be instructive benchmarks for this to occur, which is set by benchmark.isInstructive = true.',
    },
    instructiveBenchmarkRate: {
      definition:
        'The percentage of instructive benchmarks after the initial "wall" of benchmarks.',
      advice:
        "After a high initial instructive benchmark rate, taskers likely don't need as much teaching. Keep this percentage lower, around 50%.",
    },
    balancedByInstructionItem: {
      definition:
        'Whether or not benchmarks are served evenly across instructrion items (to the extent possible), regardless of their underlying distribution.',
      advice:
        'Generally good to enable by default, unless you are trying to target specific instruction item(s) to serve more frequently (e.g. if quality for one instruction item is bad).',
    },
    autoBenchmarkFromAudit: {
      definition:
        'This makes all future customer audits turn into benchmarks automatically (if they are accepted).',
    },
    benchmarkPayoutsEnabled: {},
    benchmarkPayoutsRewardAmountHundredthsOfCents: {},
    benchmarkPayoutsAccuracyTarget: {},
    benchmarkPayoutsSpeedTarget: {},
    benchmarkPayoutsPayoutWindowSize: {},
    shouldReuseBenchmarks: {
      definition:
        'Allows benchmarks to be repeated if every benchmark has already been seen be a particular tasker.',
      advice:
        'If you leave this off and you run out of new benchmarks to serve a tasker, they will just receive subtasks forever (no EQ unless you are also out of tasks). If you turn this on, be wary of the ability for taskers to "memorize" benchmarks in order to inflate their accuracy, which is more likely if the number of benchmarks is low.',
    },
    shouldThrottleOnExhaustionAttempter: {
      definition:
        'Whether or not a tasker with permissions to attempt a project should be prevented from attempting further if the tasker has completed all available attempter benchmarks for the project.',
    },
    shouldThrottleOnExhaustionL0: {
      definition:
        'Whether or not a tasker with permissions to review a project at the L0 review level should be prevented from reviewing further if the tasker has completed all available benchmarks for the project (at the L0 review level).',
    },
    shouldThrottleOnExhaustionL1: {
      definition:
        'Whether or not a tasker with permissions to review a project at the L1 review level should be prevented from reviewing further if the tasker has completed all available benchmarks for the project (at the L1 review level).',
    },
    shouldThrottleOnExhaustionL10: {
      definition:
        'Whether or not a tasker with permissions to review a project at the L10 review level should be prevented from reviewing further if the tasker has completed all available benchmarks for the project (at the L10 review level).',
    },
    disablingBenchmarkAccuracyThresholdWarnL0: {
      definition:
        'If a tasker does not achieve this level of accuracy on a particular benchmark at the L0 review level, they will see a warning about their performance.',
    },
    disablingBenchmarkAccuracyThresholdWarnL1: {
      definition:
        'If a tasker does not achieve this level of accuracy on a particular benchmark at the L1 review level, they will see a warning about their performance.',
    },
    disablingBenchmarkAccuracyThresholdWarnReviewConsensus: {
      definition:
        'If a tasker does not achieve this level of accuracy on a particular benchmark at the ReviewConsensus (L4) review level, they will see a warning about their performance.',
    },
    disablingBenchmarkAccuracyThresholdWarnL10: {
      definition:
        'If a tasker does not achieve this level of accuracy on a particular benchmark at the L10 review level, they will see a warning about their performance.',
    },
    disablingBenchmarkAccuracyThresholdDisableL0: {
      definition:
        'If a tasker does not achieve this level of accuracy on a particular benchmark at the L0 review level, then their permissions to review on this queue will be disabled.',
    },
    disablingBenchmarkAccuracyThresholdDisableL1: {
      definition:
        'If a tasker does not achieve this level of accuracy on a particular benchmark at the L1 review level, then their permissions to review on this queue will be disabled.',
    },
    disablingBenchmarkAccuracyThresholdDisableReviewConsensus: {
      definition:
        'If a tasker does not achieve this level of accuracy on a particular benchmark at the ReviewConsensus (L4) review level, then their permissions to review on this queue will be disabled.',
    },
    disablingBenchmarkAccuracyThresholdDisableL10: {
      definition:
        'If a tasker does not achieve this level of accuracy on a particular benchmark at the L10 review level, then their permissions to review on this queue will be disabled.',
    },
    disablingBenchmarkAccuracyThresholdMinTaskCountL0: {
      definition:
        'The minimum number of L0 benchmarks a tasker must do before (potentially) being warned or disabled.  Benchmark accuracy is calculated from only this number of the most recently taken benchmarks (default number is ' +
        BenchmarkDisablingDefaultThreshold.Reviewer +
        ').  Must be > 0, and should be ≤ the wall size ("L0: min benchmarks" setting) if the wall size is > 0.',
    },
    disablingBenchmarkAccuracyThresholdMinTaskCountL1: {
      definition:
        'The minimum number of L1 benchmarks a tasker must do before (potentially) being warned or disabled.  Benchmark accuracy is calculated from only this number of the most recently taken benchmarks (default number is ' +
        BenchmarkDisablingDefaultThreshold.Reviewer +
        ').  Must be > 0, and should be ≤ the wall size ("L1: min benchmarks" setting) if the wall size is > 0.',
    },
    disablingBenchmarkAccuracyThresholdMinTaskCountReviewConsensus: {
      definition:
        'The minimum number of ReviewConsensus (L4) benchmarks a tasker must do before (potentially) being warned or disabled.  Benchmark accuracy is calculated from only this number of the most recently taken benchmarks (default number is ' +
        BenchmarkDisablingDefaultThreshold.Reviewer +
        ').  Must be > 0, and should be ≤ the wall size ("ReviewConsensus: min benchmarks" setting) if the wall size is > 0.',
    },
    disablingBenchmarkAccuracyThresholdMinTaskCountL10: {
      definition:
        'The minimum number of L10 benchmarks a tasker must do before (potentially) being warned or disabled.  Benchmark accuracy is calculated from only this number of the most recently taken benchmarks (default number is ' +
        BenchmarkDisablingDefaultThreshold.Reviewer +
        ').  Must be > 0, and should be ≤ the wall size ("L10: min benchmarks" setting) if the wall size is > 0.',
    },
    disableWarnBasedOnEnabledBenchmarksOnlyReview: {
      definition: 'Restrict reviewer benchmark warning/disabling to enabled benchmarks only',
      advice:
        'False by default. Enable if you want reviewer benchmark auto-warning/-disabling agent to only consider TrainingAttempts (i.e. TaskerMetricsEntrys) from currently-enabled reviewer benchmarks on the project',
    },
    disableWarnBasedOnEvaluativeBenchmarksOnlyReview: {
      definition: 'Restrict reviewer benchmark warning/disabling to evaluative benchmarks only',
      advice:
        'False by default. Enable if you want reviewer benchmark auto-warning/-disabling agent to only consider evaluative TrainingAttempts (i.e. not instructive attempts)',
    },
    autoPromoteBenchmark: {
      definition:
        'If enabled, taskers with an attempt benchmark score >= to the specified threshold (having completed at least the minimum number of benchmarks required) will be promoted.',
    },
    autoPromoteBenchmarkAccuracyThreshold: {
      definition:
        'The minimum attempt benchmark score threshold for a tasker to be considered for promotion.',
    },
    autoPromoteBenchmarkMinTaskCount: {
      definition:
        'The minimum number of benchmarks a tasker must do before being considered for promotion. Must be set and > 0 for auto promotion to run.',
    },

    attempterAccuracyThresholdWarn: {
      definition:
        "The attempter's accuracy on non-benchmark tasks (AbsoluteAttemptAccuracy). If that accuracy falls below ___, the tasker falls into the warn threshold.",
      advice:
        "Reviewers better be accurate! In general, benchmark disabling is the norm, but if we need to scruitinize taskers' performance even further, feel free to enable this.",
    },
    attempterAccuracyThresholdDisable: {
      definition:
        "The attempter's accuracy on non-benchmark tasks (AbsoluteAttemptAccuracy). If that accuracy falls below ___, the tasker is disabled.",
      advice:
        "Reviewers better be accurate! In general, benchmark disabling is the norm, but if we need to scruitinize taskers' performance even further, feel free to enable this.",
    },
    attempterAccuracyThresholdPromote: {
      definition:
        "The attempter's accuracy on non-benchmark tasks (AbsoluteAttemptAccuracy). If that accuracy is above ___, the tasker is promoted to a reviewer.",
      advice:
        "Reviewers better be accurate! In general, benchmark disabling is the norm, but if we need to scruitinize taskers' performance even further, feel free to enable this.",
    },
    attempterAccuracyThresholdMinWarnTaskCount: {
      definition:
        'The minimum number of non-benchmark tasks before the tasker is eligible to be warned using attempter accuracy.',
      advice:
        'Give them a sufficient number of chances before they are warned. Should be less than the disabled task count.',
    },
    attempterAccuracyThresholdMinDisableTaskCount: {
      definition:
        'The minimum number of non-benchmark tasks before the tasker is eligible to be disabled using attempter accuracy.',
      advice: 'Give them a sufficient number of chances before they are disabled.',
    },
    attempterAccuracyThresholdMinPromoteTaskCount: {
      definition:
        'The minimum number of non-benchmark tasks before the tasker is eligible to be promote using attempter accuracy.',
      advice:
        'Collect a significant sample size before promoting the tasker. Should be more than the disabled task count.',
    },
    attempterSBQRateThresholdDisable: {
      definition:
        "If the attempter's SBQ rate on non-benchmark tasks is equal to or above, tasker is disabled. Enter threshold as a decimal from 0 to 1. 'Attempter SBQ Rate Task Count' is required if this is set.",
    },
    attempterSBQRateThresholdPromote: {
      definition:
        "If the attempter's SBQ rate on non-benchmark tasks falls below, tasker is promoted. Enter threshold as a decimal from 0 to 1. 'Attempter SBQ Rate Task Count' is required if this is set.",
    },
    attempterSBQRateTaskCount: {
      definition:
        'The number of most recent non-benchmark task attempts used to calculate the SBQ rate. This field is required if attempter SBQ rate threshold is set.',
    },
    reviewerSBQRateThresholdDisable: {
      definition:
        "If the reviewer's SBQ rate on non-benchmark tasks is equal to or above, tasker is disabled. Enter threshold as a decimal from 0 to 1. 'Reviewer SBQ Rate Task Count' is required if this is set.",
    },
    reviewerSBQRateThresholdDemote: {
      definition:
        "If the reviewer's SBQ rate on non-benchmark tasks is equal to or above, tasker is demoted. Enter threshold as a decimal from 0 to 1. 'Reviewer SBQ Rate Task Count' is required if this is set.",
    },
    reviewerSBQRateThresholdPromote: {
      definition:
        "If the reviewer's SBQ rate on non-benchmark tasks falls below, tasker is promoted. Enter threshold as a decimal from 0 to 1. 'Reviewer SBQ Rate Task Count' is required if this is set.",
    },
    reviewerSBQRateTaskCount: {
      definition:
        'The number of most recent non-benchmark task attempts used to calculate the SBQ rate. This field is required if a reviewer SBQ rate threshold is set.',
    },
    disablingBenchmarkAccuracyThresholdWarnAttempt: {
      definition:
        'The attempter benchmark accuracy required for taskers before they reach the warn status. When they fall below this threshold, a warning banner shows up on each task.',
      advice:
        'Depends on internal pulse of quality. Do an audit to determine the minimum accuracy required for the task to be customer-ready.',
    },
    disablingBenchmarkAccuracyThresholdSoftDisableAttempt: {
      definition:
        'The attempter benchmark accuracy required for taskers before they are soft disabled.',
      advice:
        'Depends on internal pulse of quality. Do an audit to determine the maximum accuracy of garbage tasks. We expect this value to be less than the warn value and higher than the disable value.',
    },

    disablingBenchmarkAccuracyThresholdDisableAttempt: {
      definition:
        'The attempter benchmark accuracy required for taskers before they are disabled off the project.',
      advice:
        'Depends on internal pulse of quality. Do an audit to determine the maximum accuracy of garbage tasks.',
    },

    disablingBenchmarkAccuracyThresholdMinTaskCountAttempt: {
      definition:
        'The minimum number of benchmarks a tasker must do before (potentially) being warned or disabled. Default value is ' +
        BenchmarkDisablingDefaultThreshold.Attempter +
        '. Must be > 0, and should be ≤ the wall size ("Minimum Benchmarks Attempt" setting) if the wall size is > 0.',
    },
    disableWarnBasedOnEnabledBenchmarksOnlyAttempt: {
      definition: 'Restrict attempter benchmark warning/disabling to enabled benchmarks only',
      advice:
        'False by default. Enable if you want attempter benchmark auto-warning/-disabling agent to only consider TrainingAttempts (i.e. TaskerMetricsEntrys) from currently-enabled attempter benchmarks on the project',
    },
    disableWarnBasedOnEvaluativeBenchmarksOnlyAttempt: {
      definition: 'Restrict attempter benchmark warning/disabling to evaluative benchmarks only',
      advice:
        'False by default. Enable if you want attempter benchmark auto-warning/-disabling agent to only consider evaluative TrainingAttempts (i.e. not instructive attempts)',
    },
    benchmarkChoiceThresholds: {},
    benchmarkTaskGrader: {},
    benchmarkReviewGrader: {},
    disablingBenchmarkAccuracyInstructionItemThresholds: {
      item: {
        definition: 'The ID of the instruction item.',
      },
    },
    maxNewUserPendingAttempts: {
      definition: `The number of maximum pending attempts for a tasker. Attempts are pending when the subtask is also pending. NOTE: if this value is not set, in code we default to ${MAX_NEW_USER_PENDING_ATTEMPTS}`,
      advice:
        'If the project holds a lot of tasks in L12 or some other holding stage, subtasks will be pending, and so will their attempts. This is a nice way of containing tasks as you adjust quality, but will frustrate taskers and block them from doing other subtasks.',
    },
    taskWallTaskCount: {
      definition: 'For Task Wall config, the size of the wall at which taskers are throttled.',
      advice:
        'We will throttle once the tasker has reached this number of unique tasks. Specifically, we throttle if all the latest attempts in this set of tasks are pending review.',
    },
    taskWallAllowedAttemptsPerTask: {
      definition: 'For Task Wall config, the number of allowed task attempts PER task in the wall.',
      advice:
        'For each task in the wall, this is the number of attempts the user is allowed. SBQs via routing overrides or rejections will trigger follow-up attempts. If all attempts are rejected, then the user has failed this task.',
    },
    taskWallRequireAllTasksPassed: {
      definition:
        'For Task Wall config, whether or not to permanently pass the wall if the tasker has passed all tasks in the wall, rather than the tasker passing the wall if at least one task is accepted.',
      advice:
        'If true, we permanently throttle if tasker has reached the rejection limit for ANY task. This means taskers have to pass every task to pass the wall. If false, we throttle if tasker has reached the rejection limit for ALL tasks. This means taskers have to pass at least one task to pass the wall.',
    },
    taskWallAllowAttemptsAtReviewLevels: {
      definition:
        'For Task Wall config, the review levels that the throttle will apply to. Enter as a list of numbers',
      advice:
        'The task wall will only check attempts at these review levels. If empty, the wall will only check attempts at L-1.',
    },
    taskWallSkipWorkerTags: {
      definition:
        'For Task Wall config, the task wall will not apply to any workers with any of these worker tags. Tags should be set as a list of strings, i.e. ["tag1_id", "tag2_id"].',
      advice: 'If a worker contains any of these worker tags, then they will bypass the task wall.',
    },
    minimumBenchmarksAttempt: {
      definition:
        'The minimum number of benchmarks a tasker at the attempter level must do in order to work on the queue.',
    },
    minimumBenchmarksL0: {
      definition:
        'The minimum number of benchmarks a tasker at the L0 review level must do in order to work on the queue.',
    },
    minimumBenchmarksL1: {
      definition:
        'The minimum number of benchmarks a tasker at the L1 review level must do in order to work on the queue.',
    },
    minimumBenchmarksReviewConsensus: {
      definition:
        'The minimum number of benchmarks a tasker at the ReviewConsensus (L4) review level must do in order to work on the queue.',
    },
    minimumBenchmarksL10: {
      definition:
        'The minimum number of benchmarks a tasker at the L10 review level must do in order to work on the queue.',
    },

    minimumBenchmarksPerInstructionItemAttempt: {
      definition:
        'This is the minimum number of benchmarks per ii an attempter must do in order to work on the queue. This can be configured more granularly by setting `minimumBenchmarksForInstructionItemsAttempt` and `minimumBenchmarksPerInstructionItemTimeWindowAttempt`.',
    },
    minimumBenchmarksPerInstructionItemL0: {
      definition:
        'This is the minimum number of benchmarks per ii an L0 reviewer must do in order to work on the queue. This can be configured more granularly by setting `minimumBenchmarksForInstructionItemsL0` and `minimumBenchmarksPerInstructionItemTimeWindowL0`.',
    },
    minimumBenchmarksPerInstructionItemL1: {
      definition:
        'This is the minimum number of benchmarks per ii an L1 reviewer must do in order to work on the queue. This can be configured more granularly by setting `minimumBenchmarksForInstructionItemsL1` and `minimumBenchmarksPerInstructionItemTimeWindowL1`.',
    },
    minimumBenchmarksPerInstructionItemL10: {
      definition:
        'This is the minimum number of benchmarks per ii an L10 reviewer must do in order to work on the queue. This can be configured more granularly by setting `minimumBenchmarksForInstructionItemsL10` and `minimumBenchmarksPerInstructionItemTimeWindowL10`.',
    },
    ignorePreviouslyCompletedBenchmarksForBenchmarkWallAfterRebirth: {
      definition:
        'This is a boolean indicating whether you want to ignore previously completed benchmarks in the benchmark wall for a given tasker after it is rebirthed. Under the hood, this is implemented by using the Tasker Metrics Entry as the source of truth instead of the Training Attempts, since the Tasker Metrics Entry gets wiped after rebirth for the given tasker.',
    },
    benchmarkServeTraineesBenchmarks: {
      definition:
        'Trainees have traditionally been exempt from ALL benchmarks. Set this flag to true to force them to get benchmarks.',
    },
    showBenchmarkAccuracyBar: {
      definition: 'In Remotasks, show their benchmark accuracy in the Projects tab.',
      advice:
        "Don't do this. This will allow spammers to game the minimum amount of tasks they actually need to do until they get disabled.",
    },
    autocreateBenchmarkFromReview: {
      definition: 'Review level to autocreate benchmark from',
      advice: `Since this relies on a task hook, you must also add the hook via the config 'Task Hook Config' -> 'Run on subtask completion' -> ['autocreate_benchmarks_from_review']. Please see https://github.com/scaleapi/scaleapi/pull/26783 or message Jeffrey Yun for questions.`,
    },
    enableAccuracyBuckets: {
      definition:
        'This splits up tasker accuracies into different buckets dictated by their max hourly pay, such that lower accuracy taskers are compensated less than higher accuracy taskers. If this field is checked, taskers must increase quality (before throughput) in order to increase their pay.',
    },
    softDisablingConfigEnabled: {
      definition:
        "Whether soft disabling exists for this project. The below 2 configs won't work unless this is on.",
    },
    softDisablingConfigMaxBenchmarks: {
      definition:
        'The max number of BMs a tasker can be served in soft disabling mode, before being kicked off the project (if they were not able to sufficiently improve their accuracy).',
      advice:
        "This should be set fairly high, as it doesn't hurt to have taskers who are soft disabled.",
    },
    softDisablingConfigInstructiveBenchmarkRate: {
      definition:
        'The percentage of BMs that are instructive (tasker can see answer key) when in soft disabling mode.',
      advice:
        'This should be set quite high (often between 0.85 and 1), as we want taskers to learn & improve while in soft disabling mode.',
    },
    softDisablingConfigCourses: {
      definition:
        'Array of course IDs to be served upon entering soft-disabled state. Taskers must complete these courses before continuing to task',
    },
    softDisablingConfigMaxCount: {
      definition:
        'Number of times you can enter soft-disabling before being automatically disabled, default is 100',
    },
    responseLinterConfigEnabled: {},
    responseLinterConfigRules: {
      definition:
        'Each rule represents one linter script configured a certain way. Note that a project can have multiple linter scripts, and even the same linter multiple times (configured in different ways).',
      // nested
      name: {
        definition:
          'Add a name to describe what the linter does or checks for. This name should be unique.',
      },
      condition: {
        definition:
          'Add the JSON object that configures the linter. The script name must exactly match an existing linter script name.',
      },
      level: {
        definition:
          "These are the actions that can occur when a task fails a post-submission (backend) linter.\n**Discard:** throws away the task attempt, and adds the worker to the excluded workers list.\n**Escalate:** accepts the task attempt, but escalates it to the corp flagged review level (L11).\n**Ban tasker:** bans the tasker from this specific project.\n**Info:** does not affect the task. This setting is used to track metrics or to test out a linter.\n**Exclude from consensus:** creates an attempt, but excludes it from consensus if the 'Exclude marked task attempts from consensus' project config is on.\n**Redo:** sends the task back to the same level but with the previous response.\n**Send Back to Attempt:** Currently only works for 3D lidar. Sends the task back to attempt level, with the previous response.",
      },
      stage: {
        definition:
          'This field configures when the linter will run.\n**Pre-submission:** runs the linter before submission and shows the tasker any flagged errors. This was previously known as “frontend” linting.\n**Post-submission:** runs the linter after the task is submitted and takes an action based on the selected “Post-submission action”. This was previously known as “backend” linting.\n**Both:** The linter will run on both stages.',
      },
      frontendSeverity: {
        definition:
          'These are the actions that can occur when a task fails a pre-submission linter.\n**Blocking error:** Tasker sees a non-dismissable error that must be fixed before they can submit the task.\n**Dismissable error:** Tasker sees an error box but can still submit the task without fixing the error.\n**Debug:** hides the error message from the tasker. It is used by the engineering team for debugging purposes.\n**Runtime exception:** a special setting used by the engineer team for linters that may cause errors in the code.',
      },
      enableAttempt: {
        definition: 'Whether to enable this linter at the attempt level.',
      },
      enableL0: {
        definition: 'Whether to enable this linter at the L0 level.',
      },
      enableL1: {
        definition: 'Whether to enable this linter at the L1 level.',
      },
      enableL4: {
        definition: 'Whether to enable this linter at the L4 level.',
      },
      enableL8: {
        definition: 'Whether to enable this linter at the L8 level.',
      },
      enableL10: {
        definition: 'Whether to enable this linter at the L10 level.',
      },
      enableL11: {
        definition: 'Whether to enable this linter at the L11 level.',
      },
      enableL12: {
        definition: 'Whether to enable this linter at the L12 level.',
      },
      enableBenchmarks: {
        definition:
          'Whether to enable this linter at **Benchmarks** (you also need to enable the linter on some review level)',
      },
      message: {
        definition:
          'Message will be displayed to the tasker for frontend linters, in the form **message**: **issues**, so something descriptive like "Please check the following areas for stray pixels" works best.',
      },
      trackPassRate: {
        definition: 'Stores in TME the pass status for each individual task attempt.',
        advice:
          'This can be very high data volume -- afaik, the only other way to track pass rate is Datadog logs. @jeffyun has some ideas: tinyurl.com/linterpassratetracking',
      },
      showPreviousFailures: {
        definition: "Whether to show an attempt's failed lint results to the next tasker",
      },
      enableLintAutofix: {
        definition: 'Whether to enable autofix suggested actions for this linter',
      },
      lintPanelOrder: {
        definition:
          'Importance score compared to other lint errors (defaults to 0). Lint errors are displayed in order of highest to lowest priority in new lint panel (ties are broken by first linter to finish running).',
      },
      disabledDefaultLinterReason: {
        definition:
          'Only complete this field if a validation asks for it.\n**testing**: I am testing out this linter\n**in_progress**: I am still configuring my project\n**need_more_info**: I don’t understand this linter\n**unexpected_behavior**: this linter is not behaving as expected (over or underflagging)\n**unnecessary**: this linter does not fit my project rules or needs',
      },
      useCase: {
        definition:
          '**informational**: These are linters that we expect to flag some correct and some incorrect tasks, and send them to L11 for greater review. They might have a high flag rate and are usually designed to screen all tasks with a certain attribute or label into L11.\n**Critical**: there are linters that we expect to always flag incorrect tasks. They should never flag correct tasks. They are usually set to ERROR instead of ESCALATE.\n**Other**: Other types of linters that do not fit the two main categories.',
      },
    },
    mlFastLinterConfigs: {
      definition:
        'Enable ML fast linters. These are ML controlled lint endpoints. Multiple can be enabled on a project',
      advice: 'none',
      channelSuffix: {
        definition:
          'All linters work on NATS channel with the from `fast.doclinter.<suffux>`. Specify the suffix here.',
      },
    },
    guidedWorkflowDefinitionConfigRules: {
      definition:
        'Each Definition is one markdown file that is served to the tasker. Note - each review level can only have one enabled markdown. If there is more, system takes the first available.',
      // nested
      name: {
        definition:
          'Name should be a unique identifier for the markdown. Used to identify in logging (wip)',
      },
      workflowMarkdown: {
        definition:
          'Markdown to be used to render the workflow. Do not format in this textbox, copy and paste preferred. Please refer to: https://scale-api.quip.com/Gmr0AYkjqwo2/Guided-Workflow-Markdown-Syntax',
      },
      enableAttempt: {
        definition: 'Whether to enable this workflow at the attempt level.',
      },
      enableL0: {
        definition: 'Whether to enable this workflow at the L0 level.',
      },
      enableL1: {
        definition: 'Whether to enable this workflow at the L1 level.',
      },
      enableL10: {
        definition: 'Whether to enable this workflow at the L10 level.',
      },
      enableL11: {
        definition: 'Whether to enable this workflow at the L11 level.',
      },
      enableL12: {
        definition: 'Whether to enable this workflow at the L12 level.',
      },
      enableTrainingScenario: {
        definition: 'Whether to enable this workflow on training scenarios.',
      },
      isRequiredForTrainingScenario: {
        definition:
          'Whether to block submission until workflow is completed on training scenarios.',
      },
    },
    internalInstruction: {
      definition:
        'Internal Instructions work like regular instructions, but are hidden to the customer and stored at the subtask level.',
      advice:
        'Internal Instructions completely overwrite the customer provided instructions. Internal Instructions are stored on the `subtask.params.internalInstruction` field',
    },
    showBenchmarkPassRate: {},
    dailySkipLimit: {
      definition: 'Number of tasks a tasker is allowed to skip without penalty.',
      advice:
        "Be forgiving! The standard is 5. When building up a project, subtasks can be poorly formatted and cause frustration. If taskers can't skip tasks when they're completely stuck, then they lose faith in the project.",
    },
    allowLidarSkipsAtAttempt: {
      definition: 'Can Lidar Taskers Skip tasks at the attempt level?',
      advice:
        'Lidar is weird, as per usual, and the default behavior is that attempters cannot skip. It is HIGHLY recommended that you do not set this without setting a max number of skips, other wise it is succeptible to abuse.',
    },
    dailyBenchmarkSkipLimit: {
      definition: 'Number of benchmarks a tasker is allowed to skip without penalty.',
      advice: 'If benchmarks are well-tested, this number should be close to 1.',
    },
    bypassMultitouch: {
      definition: 'Allows for same tasker to review their own attempt on a given task.',
      advice:
        'This project config will supersede any project/tasker feature flag that bypasses multitouch',
    },
    prebuildAttempts: {
      definition: 'Enable or disable prebuilding for attempts.',
    },
    prebuildReviews: {
      definition: 'Enable or disable prebuilding for reviews.',
    },
    prebuildUseAutoPromotion: {
      definition:
        'Attempters are promoted to reviewers upon completion of their initial benchmarks. Prebuilding needs to be enabled for both attempters and reviewers.',
      advice:
        'The Benchmark Task Accuracy Threshold section should be filled in to prevent poor prebuild attempters from becoming reviewers. Under these conditions, auto-promoting attempters to reviewers is worthwhile to find quality reviewers. ',
    },
    prebuildExpiresAt: {
      definition: 'This date defines when the prebuild phase is over. This *MUST* be set.',
      advice:
        'Set it to the duration of your prebuild experiments. This field is mandatory to prevent excessive payouts.',
    },
    holdDeliverableTasks: {
      definition:
        'If this is enabled, all tasks submitted by an L10 auditor will hold in L12 layer, and must be manually released to customer.',
      advice:
        'Its safest to have L12 on to prevent accidental submits from going to customer, but note that lead auditors cannot make changes in L12 unless manually permissioned to L12 first. -- Thanks Courtney J. for contributing the definition and advice!',
    },
    smartSampleAttempt: {
      definition:
        'Whether to hold attempts in `subtask.status=auditing`, instead of sending them directly to higher review levels.',
      advice:
        'Config has the form `{ strategy: strategyName, enabled: true/false, rate: number }`; please refer to https://scale-api.quip.com/p4CvArcpvKDe/Wiki-Smart-Sampling for the specific sampling strategies. Rate=fraction of attempts held - for example, `rate=0.9` => 90% of attempts are held in the `auditing` stage and 10% of attempts go on automatically to higher review levels.',
    },
    smartSampleL0: {
      definition:
        'Whether to hold L0 reviews in `subtask.status=auditing`, instead of sending them directly to higher review levels.',
      advice:
        'Config has the form `{ strategy: strategyName, enabled: true/false, rate: number }`; please refer to https://scale-api.quip.com/p4CvArcpvKDe/Wiki-Smart-Sampling for the specific sampling strategies. Rate=fraction of L0 reviews held - for example, `rate=0.9` => 90% of L0 reviews are held in the `auditing` stage and 10% of L0 reviews go on automatically to higher review levels.',
    },
    smartSampleL1: {
      definition:
        'Whether to hold L1 reviews in `subtask.status=auditing`, instead of sending them directly to higher review levels.',
      advice:
        'Config has the form `{ strategy: strategyName, enabled: true/false, rate: number }`; please refer to https://scale-api.quip.com/p4CvArcpvKDe/Wiki-Smart-Sampling for the specific sampling strategies. Rate=fraction of L1 reviews held - for example, `rate=0.9` => 90% of L1 reviews are held in the `auditing` stage and 10% of L1 reviews go on automatically to higher review levels.',
    },
    smartSampleL10: {
      definition:
        'Whether to hold L10 reviews in `subtask.status=auditing`, instead of sending them directly to higher review levels.',
      advice:
        'Config has the form `{ strategy: strategyName, enabled: true/false, rate: number }`; please refer to https://scale-api.quip.com/p4CvArcpvKDe/Wiki-Smart-Sampling for the specific sampling strategies. Rate=fraction of L10 reviews held - for example, `rate=0.9` => 90% of L10 reviews are held in the `auditing` stage and 10% of L10 reviews go on automatically to higher review levels.',
    },
    claimTimeMaxIdleMinutesAttempt: {
      definition:
        'The amount of time a user can be idle on a task at the attempt level before losing their claim on the task',
      advice: 'This should generally be set to the length of a reasonable break (e.g. 60 minutes)',
    },
    claimTimeMaxIdleMinutesL0: {
      definition:
        'The amount of time a user can be idle on a task at the L0 level before losing their claim on the task',
      advice: 'This should generally be set to the length of a reasonable break (e.g. 60 minutes)',
    },
    claimTimeMaxIdleMinutesL1: {
      definition:
        'The amount of time a user can be idle on a task at the L1 level before losing their claim on the task',
      advice: 'This should generally be set to the length of a reasonable break (e.g. 60 minutes)',
    },
    claimTimeMaxIdleMinutesReviewConsensus: {
      definition:
        'The amount of time a user can be idle on a task at the ReviewConsensus (L4) level before losing their claim on the task',
      advice: 'This should generally be set to the length of a reasonable break (e.g. 60 minutes)',
    },
    claimTimeMaxIdleMinutesL8: {
      definition:
        'The amount of time a user can be idle on a task at the L8 level before losing their claim on the task',
      advice: 'This should generally be set to the length of a reasonable break (e.g. 60 minutes)',
    },
    claimTimeMaxIdleMinutesL10: {
      definition:
        'The amount of time a user can be idle on a task at the L10 level before losing their claim on the task',
      advice: 'This should generally be set to the length of a reasonable break (e.g. 60 minutes)',
    },
    claimTimeMaxHoldMinutesAttempt: {
      definition:
        'The amount of time a user has to complete a task at the attempt level from when they start working on it',
      advice: `This should be set to the maximum amount of time it takes to complete a task start to finish.
      It is generally safe to use a large value here, since the tasker will lose their claim if they are not actively working on the task
      (unless you've set the corresponding "max idle minutes" to a large value, which you should generally never do)`,
    },
    claimTimeMaxHoldMinutesL0: {
      definition:
        'The amount of time a user has to complete a task at the L0 level from when they start working on it',
      advice: `This should be set to the maximum amount of time it takes to complete a task start to finish.
      It is generally safe to use a large value here, since the tasker will lose their claim if they are not actively working on the task
      (unless you've set the corresponding "max idle minutes" to a large value, which you should generally never do)`,
    },
    claimTimeMaxHoldMinutesL1: {
      definition:
        'The amount of time a user has to complete a task at the L1 level from when they start working on it',
      advice: `This should be set to the maximum amount of time it takes to complete a task start to finish.
      It is generally safe to use a large value here, since the tasker will lose their claim if they are not actively working on the task
      (unless you've set the corresponding "max idle minutes" to a large value, which you should generally never do)`,
    },
    claimTimeMaxHoldMinutesReviewConsensus: {
      definition:
        'The amount of time a user has to complete a task at the ReviewConsensus (L4) level from when they start working on it',
      advice: `This should be set to the maximum amount of time it takes to complete a task start to finish.
      It is generally safe to use a large value here, since the tasker will lose their claim if they are not actively working on the task
      (unless you've set the corresponding "max idle minutes" to a large value, which you should generally never do)`,
    },
    claimTimeMaxHoldMinutesL10: {
      definition:
        'The amount of time a user has to complete a task at the L10 level from when they start working on it',
      advice: `This should be set to the maximum amount of time it takes to complete a task start to finish.
      It is generally safe to use a large value here, since the tasker will lose their claim if they are not actively working on the task
      (unless you've set the corresponding "max idle minutes" to a large value, which you should generally never do)`,
    },
    claimTimeMaxHoldMinutesL8: {
      definition:
        'The amount of time a user has to complete a task at the L8 level from when they start working on it',
      advice: `This should be set to the maximum amount of time it takes to complete a task start to finish.
      It is generally safe to use a large value here, since the tasker will lose their claim if they are not actively working on the task
      (unless you've set the corresponding "max idle minutes" to a large value, which you should generally never do)`,
    },
    pauseTimerEnabled: {
      definition:
        'Allows the project to pause the timer for a tasker when they are not actively working on a task.',
      advice: `THIS FEATURE SHOULD ONLY BE ENABLED FOR ONSITE WORKERS. This should be set to true if you want to allow taskers to pause the timer when they are not actively working on a task. This is
      recommended for tasks that are long and warrant longer breaks.`,
    },
    autoPauseEnabled: {
      definitions: '[WIP] Enables the auto-pause feature for the object.',
    },
    zeroWorkReclaimDisabled: {
      definition:
        'Whether a tasker is allowed to reclaim a subtask after their claim expires and zero work was done.',
      advice:
        'Turning this on will prevent the same tasker from claiming a subtask over and over without doing work. At the same time, sometimes people are legitimately idle on a claimed subtask.',
    },
    longTailSubtaskAttemptCutoff: {
      definition:
        'The number of attempts a subtask has before it is considered "long tail" for alerting purposes in #long-tail-task-alerts and corp recall tools.',
      advice: `It is helpful to set this to a reasonable number relating to the max number of attempts for a subtask in
      order to be alerted properly. For example, if a subtask has 100 attempts, recalling it probably won't fix the issue, and will just add more attempts on the subtask.`,
    },
    depermissionWageCutoff: {
      definition:
        'The Dollars pay per hour before a tasker is automatically depermissioned from the project. Anything lower will be depermissioned.',
      advice: `Every day, https://redash.scale.com/queries/5135/ will run and depermission taskers with wage lower than the cutoff.
      This is helpful to depermission taskers that are not adding much value to the task. Set to 0 to disable.`,
    },
    enableZeroReview: {
      definition: '',
      advice: '',
    },
    enableSpotterInlineComments: {
      definition: 'If true, spotters will be able to give inline comments',
      advice: 'Use this when zero review is enabled',
    },
    usePerAnnotationAuditUi: {
      definition: 'Whether to use the per annotation audit UI for customer audits.',
      advice: 'This is only supported for 2D and 3D lidarAnnotation task types.',
    },
    codingWorkspaceImageVersion: {
      definition: `The current version of the coding workspace to use. Upon updating the version number, all new workspaces will be created with the new version number, and all existing workspaces with the previous version number will be scrapped. `,
      advice:
        'Note that the version number just needs to be different than the last, but it is recommended you incremement by 1 to record the order of the versions.',
    },
    textCollectionInsightsDashboardEnabled: {
      definition: 'Whether to enable the text collection insights dashboard.',
      advice: `This was originally designed for roadrunner and then generalized to all text collection projects.`,
    },
    textCollectionInsightsDashboardInactive: {
      definition:
        'Whether an insights dashboard is inactive (set this to stop re-computing insights for this project)',
      advice: `When true, we ignore this project in our recurring cron job to re-compute insights for all projects.`,
    },
    textCollectionTaxonomyManagementEnabled: {
      definition: 'Whether to enable the text collection taxonomy management tooling.',
      advice: `Allow customers <> operators to iterate and version taxonomies.`,
    },
    enableFixedPerAnnotationErrors: {
      definition: 'Whether to enable fixed or auto-detected per annotation errors.',
      advice: 'This is only supported for 2D task types.',
    },
    perAnnotationAuditErrorTypes: {
      definition: 'Custom feedback error types & error type subcategories.',
      advice: `Custom error types and optionally have subcategories and have the form of:
      {errorType: string, subErrorTypes?: string[]}[].

      Only available for 3D tasks.

      Example with two error types:
      [{"errorType": "Geometry", "subErrorTypes": ["Height", "Width", "Depth"]}, {"errorType": "Label"}]`,
    },
    perAnnotationAuditSeverity: {
      definition: 'Custom feedback severity levels and weighting.',
      advice: `Custom severity levels and severity level weighting. Weights are used in fixless audit grading and this config is only available for 3D tasks. Note: During grading, precision scores are normalized and divided by absolute, non-normalized values according to the 3D Fixless Audits Confluence doc. If a large range of weights are used, your precision scores will lean towards 1 and thus be less representative. We recommend using a small range/spread of weights.

      Example:
      [{"name": "Low", "weight":0.2}, {"name":"Medium", "weight": 0.3}, {"name":"High", "weight": 0.5}]`,
    },
    showCustomerAuditDiffs: {
      definition: 'Whether to show the customer audit diff in the customer audit UI.',
    },
    assignRejections: {
      definition:
        'If true, when a task is rejected, it will be assigned to the previous tasker who worked on the task',
    },
    rejectionSameAssignCount: {
      definition:
        'If assign rejections is true, the number of times a task will be assigned to the prevous tasker on rejection',
    },
    projectPlatform: {
      definition:
        'The platform the project is completed on. Determines whether a project is designed as on (Scale) or off-platform (everything else)',
    },
    disableFromWorkerPools: {
      definition:
        'Toggle on to prevent the project from being assigned to qualified workers on other teams who run out of tasks for their immediately assigned projects.',
    },
    throttleWorkerAttemptThrottleEnabled: {
      definition:
        'Whether or not throttles are currently in effect. Please note that even if maximum values are provided below, if "enabled" is not turned on, all other entries will have no effect',
    },
    throttleMaximumTaskAttemptsPerWorkPeriod: {
      definition:
        'Used together with "Work Period Length," this provides the ability to set a custom time period in which the provided number of attempts for an individual tasker cannot be exceeded. By default, this is set to 100 if no value is provided and throttles are enabled.',
    },
    throttleWorkPeriodLength: {
      definition:
        'Used together with "Max Attempts Per Work Period," this provides the ability to set a custom time period in which the provided number of attempts for an individual tasker cannot be exceeded. By default, this is set to 24hrs if no value is provided and throttles are enabled.',
    },
    throttleMaximumTaskAttemptsPerWeek: {
      definition:
        'The maximum number of tasks that an individual tasker can complete per calendar week.',
    },
    throttleMaximumTaskAttemptsPerDay: {
      definition: 'The maximum number of tasks that an individual tasker can complete per day.',
    },
    throttleMaximumTaskAttemptsPerHour: {
      definition: 'The maximum number of tasks that an individual tasker can complete per hour.',
    },
    throttleUserTagMaxAttempts: {
      definition:
        'The maximum number of tasks that a new user can complete. If work period length is not specified, then this is a cap on total attempts. If work period length is specified, then this is a cap per work period.',
    },
    throttleUserTagId: {
      definition: 'The tag ID for users that should be throttled (e.g. 65affd95d848038e224c416f)',
    },
    throttleReviewLevelAttemptMaxAttemptsPerWorkPeriod: {
      definition:
        'The maximum number of tasks that an individual tasker can complete per work period at the attempt level.',
    },
    throttleReviewLevelNormalMaxAttemptsPerWorkPeriod: {
      definition:
        'The maximum number of tasks that an individual tasker can complete per work period at the normal level.',
    },
    throttleReviewLevelLevel1MaxAttemptsPerWorkPeriod: {
      definition:
        'The maximum number of tasks that an individual tasker can complete per work period at level 1.',
    },
    throttleReviewLevelReviewConsensusMaxAttemptsPerWorkPeriod: {
      definition:
        'The maximum number of tasks that an individual tasker can complete per work period at the review consensus level.',
    },
    throttleReviewLevelCorpMaxAttemptsPerWorkPeriod: {
      definition:
        'The maximum number of tasks that an individual tasker can complete per work period at the corp level.',
    },
    throttleWorkerBenchmarkAttempt: {
      definition: 'The maximum number of benchmarks that an individual tasker can complete.',
    },
    autoremoveTaskerConfig: {
      definition:
        'Config to move workers from one WT to another when they are "low" quality. Low quality is defined as having completed X tasks, with the last Y tasks having an avg accuracy below Z',
    },
    bpcEncodingEnabled: {
      definition: 'Whether or not to enable binary attachment processing.',
    },
    customerCanRedoTasks: {
      definition: 'Whether the customer can request to redo tasks through the API or UI',
      advice: `Default is false. Make sure the correct billing spec is set up beforehand to properly charge for these redos.`,
    },
    redoLevel: {
      definition: 'The review level redo tasks are sent to',
      advice: `Default is L0`,
    },
    inlineCommentsFromReview: {
      definition: 'The minimum review level inline comments are enabled on.',
      advice:
        'Default is L10. If set to -1, inline comments will be enabled at all levels including attempt. Send back to queue with changes will be enabled for the same review level to send comments back to the queue.',
    },
    priorityLabels: {
      definition: 'The priority labels to be recommended to the tasker.',
      advice:
        'Provide the label display names to recommend. They should be formatted as a list of strings, for example ["label1", "label2"].',
    },
    choiceDisplayMap: {
      definition:
        'The map used to overrides the current display label with the value for the corresponding display key.',
      advice:
        'If the value of this map is { "car": "car1", "truck": "truck1" } then the labeler will show replace the "car" and "truck" labels with "car1" and "truck1" respectively.',
    },
    autoSaveWorkToS3: {
      definition:
        'Whether to automatically save current work on an in-progress task to a remote place (S3), which *ALL* users see on loading the task.',
      advice:
        '***OFF by default for text-collection projects, ON for other task types*** This is useful for long tasks: when anyone opens a task, any progress that has been saved remotely by ANY USER beforehand will be loaded into the UI automatically.',
    },
    textCollectionEmbeddingsLink: {
      definition:
        'To enable semantic category search in textCollection tasks, we might want different word embeddings depending on the kind of categories. This is a link to the S3 artifact of some embeddings.',
      advice:
        'Link to an embeddings file of the appropriate dimensions. Some available embeddings (replace X with 10, 20, 30, 40, 50 or 60): https://static.remotasks.com/uploads/628d124dc54954002b0b0115/pca_embeddings_X.json. These are generated w.r.t. the Instacart large taxonomy, which should work fine as a general-purpose option.',
    },
    textCollectionUseSemanticSearch: {
      definition: 'Use semantic search for text collection projects with multi-selectable choices',
    },
    textCollectionSmartAssistantReviewLevels: {
      definition:
        'Enable LLM-powered smart assistant chatbot for text collection projects at these review levels',
    },
    textCollectionSmartAssistantPrompt: {
      definition:
        'Prepended (optional) prompt for the LLM-powered smart assistant chatbot for text collection projects',
    },
    textCollectionSmartAssistantInstructionsS3Document: {
      definition:
        'Support searching across project instructions. Follow the instructions here https://scale.atlassian.net/wiki/spaces/OP/pages/321781817/Smart+Assistant#Project-Instructions-Search',
    },
    textCollectionRandomizeAttachmentOrder: {
      definition:
        'If multiple attachments are present, this config randomizes the order of these attachments in the Remotasks',
    },
    textCollectionRandomizeAttachmentOrderIndices: {
      definition:
        'If you only want to randomize specific attachments, specify the indices (comma-separated) of the attachments to randomize. For example, if you have 3 attachments and you want to randomize the first and third attachments, specify "0, 2"',
    },
    textCollectionCanSelectIntermediateNodes: {
      definition: 'Allows selecting intermediate nodes for all fields with nested taxonomies',
    },
    textCollectionDisableIntermediateAutoSelect: {
      definition:
        'Disables auto-selecting all intermediate nodes of a selected leaf node in a nested taxonomy field',
    },
    textCollectionPersistSearchQueryAfterSelect: {
      definition: 'Persists the search query text after a choice is selected when tasking',
    },
    textCollectionInnerTextLinksToRecordClicks: {
      definition:
        'Record clicking links by specifying the phrases the links are embedded in within the markdown attachment.',
    },
    textCollectionStyleSelectedChoicesBorders: {
      definition:
        'Style selected choices with borders. This is a pre-requisite for the CAIM audit workflow',
    },
    textCollectionAcceptableAuditWorkflow: {
      definition:
        'CAIM (correct, acceptable, incorrect, missing) audit workflow. When auditing, customers will be able to mark answers as one of these options. Useful for maintaining the concept of "acceptable" answers when auditing',
    },
    textCollectionGroupSameLabelChoices: {
      definition: 'In UX, groups choices with the same label but different values (paths)',
    },
    textCollectionShowPreviousResponsesForTextFields: {
      definition:
        'Show previous responses for text fields in the UX. Only applicable for review tasks. Will appear for all text fields.',
    },
    textCollectionUseRawImageSize: {
      definition: 'Display image attachments using original size',
    },
    automateMetricsFilters: {
      definition:
        'List of filter functions that automatically run periodically on taskers and attempts in this project, based on custom tasker metrics defined in code. Then, run some possible actions on them as well. For example: add a filter that queries for taskers of unwanted country codes, and block them from accessing the relevant task batches:\n\n<<https://docs.google.com/document/d/1WOEy3POoUTfxRk8agaTNEGy3EqiH-G9GsDN77d6zpco/edit?usp=sharing>>',
      advice:
        'The filters are run in order, and the first filter that matches the attempt or worker will be used.',
      name: {
        definition: 'The name of the filter',
      },
      params: {
        definition: 'The params for the filter',
        advice:
          'Check the filter documentation for the required params for each filter:<<https://docs.google.com/document/d/1WOEy3POoUTfxRk8agaTNEGy3EqiH-G9GsDN77d6zpco/edit?usp=sharing>>',
      },
      actionConfig: {
        definition:
          'The action config for the filter. This determines what action to take when the filter matches, e.g. block taskers, reject their attempts, etc.',
        advice: `Default: {
          "blockWorkers": true,
          "payWorkers": false,
          "cancelAttempts": true
        }`,
      },
    },
    mainFrameInfo: {
      definition: 'Info text to be displayed on top of the main image in imageannotation projects.',
    },
    contextFrameInfo: {
      definition:
        'Info text to be displayed on top of the context image in imageannotation projects.',
    },
    colorDefault: {
      definition:
        'Default to set the color by to in image and video projects. It should have the same name (case-sensitive) as the color by field you would like to set it to (e.g., id, label, etc.).',
    },
    skipBenchmarkWallWorkerTags: {
      definition:
        'Worker tag IDs that are allowed to skip the benchmark wall. If a worker has any of these tag ids, they will not be required to complete the benchmark wall. Tags should be set as a list of strings, i.e. ["tag1_id", "tag2_id"].',
    },
    presentationDashboardPages: {
      definition:
        'Adding an item here will create a new tab with the provided page name on the legacy customer dashboard. The page will display the embedded URL in an iframe that the customer can easily view. URLs must be from a google docs file (e.g. https://docs.google.com/...).',
    },
  },
};
